if (!customElements.get('charm-builder')) {
  customElements.define(
    'charm-builder',
    class CharmBuilder extends HTMLElement {
      constructor() {
        super();
        this.maxCharms = parseInt(this.dataset.maxCharms || '6', 10);
        this.selectedBase = null;
        this.selectedCharms = [];

        this.tabs = this.querySelectorAll('.charm-builder__tab');
        this.basePanels = this.querySelectorAll('[data-base-panel]');
        this.baseCards = this.querySelectorAll('.charm-builder__base-card');
        this.charmCards = this.querySelectorAll('.charm-builder__charm-card');

        this.summaryBase = this.querySelector('[data-summary-base]');
        this.summaryCharms = this.querySelector('[data-summary-charms]');
        this.summaryTotal = this.querySelector('[data-summary-total]');
        this.addButton = this.querySelector('[data-add-to-cart]');
        this.statusEl = this.querySelector('[data-status]');

        this.bindTabs();
        this.bindBaseCards();
        this.bindCharmCards();
        this.bindAddToCart();
      }

      bindTabs() {
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            this.tabs.forEach((t) => t.classList.remove('is-active'));
            tab.classList.add('is-active');
            const type = tab.dataset.baseType;
            this.basePanels.forEach((panel) => {
              panel.hidden = panel.dataset.basePanel !== type;
            });
          });
        });
      }

      bindBaseCards() {
        this.baseCards.forEach((card) => {
          card.addEventListener('click', () => {
            if (card.disabled) return;
            this.baseCards.forEach((c) => c.classList.remove('is-selected'));
            card.classList.add('is-selected');
            this.selectedBase = {
              id: card.dataset.baseId,
              title: card.dataset.baseTitle,
              price: parseInt(card.dataset.basePrice, 10),
              image: card.dataset.baseImage,
            };
            this.renderSummary();
          });
        });
      }

      bindCharmCards() {
        this.charmCards.forEach((card) => {
          const select = card.querySelector('[data-charm-variant-select]');
          const toggleBtn = card.querySelector('[data-charm-toggle]');
          const priceEl = card.querySelector('[data-charm-price]');
          const imageEl = card.querySelector('[data-charm-image-el]');

          if (select) {
            select.addEventListener('change', () => {
              const opt = select.selectedOptions[0];
              toggleBtn.dataset.charmId = opt.value;
              toggleBtn.dataset.charmPrice = opt.dataset.price;
              toggleBtn.dataset.charmVariantTitle = opt.dataset.title;
              toggleBtn.disabled = opt.disabled;
              if (priceEl) priceEl.textContent = this.formatMoney(parseInt(opt.dataset.price, 10));

              if (opt.dataset.image) {
                card.dataset.charmImage = opt.dataset.image;
                if (imageEl) imageEl.src = opt.dataset.image;
              }

              if (card.classList.contains('is-selected')) {
                if (opt.disabled) {
                  this.removeCharmCard(card);
                } else {
                  const entry = this.selectedCharms.find((c) => c.uid === card);
                  if (entry) {
                    entry.id = opt.value;
                    entry.price = parseInt(opt.dataset.price, 10);
                    entry.title = `${card.dataset.charmTitle} — ${opt.dataset.title}`;
                    entry.image = card.dataset.charmImage;
                    this.renderSummary();
                  }
                }
              }
            });
          }

          toggleBtn.addEventListener('click', () => {
            if (toggleBtn.disabled) return;

            if (card.classList.contains('is-selected')) {
              this.removeCharmCard(card);
              this.renderSummary();
              return;
            }

            if (this.selectedCharms.length >= this.maxCharms) {
              this.setStatus(`You can add up to ${this.maxCharms} charms per build.`, 'error');
              return;
            }

            const variantTitle = toggleBtn.dataset.charmVariantTitle;
            this.selectedCharms.push({
              uid: card,
              id: toggleBtn.dataset.charmId,
              title: variantTitle ? `${card.dataset.charmTitle} — ${variantTitle}` : card.dataset.charmTitle,
              price: parseInt(toggleBtn.dataset.charmPrice, 10),
              image: card.dataset.charmImage,
            });
            card.classList.add('is-selected');
            toggleBtn.textContent = 'Remove';
            this.renderSummary();
          });
        });
      }

      removeCharmCard(card) {
        const idx = this.selectedCharms.findIndex((c) => c.uid === card);
        if (idx > -1) this.selectedCharms.splice(idx, 1);
        this.resetCardUI(card);
      }

      resetCardUI(card) {
        card.classList.remove('is-selected');
        const toggleBtn = card.querySelector('[data-charm-toggle]');
        if (toggleBtn) toggleBtn.textContent = 'Add';
      }

      renderSummary() {
        // Base
        if (this.selectedBase) {
          this.summaryBase.classList.add('has-item');
          this.summaryBase.innerHTML = `
            <img src="${this.selectedBase.image}" alt="" width="54" height="54">
            <div class="charm-builder__summary-base-text">
              <div class="title">${this.selectedBase.title}</div>
              <div class="price">${this.formatMoney(this.selectedBase.price)}</div>
            </div>
          `;
        } else {
          this.summaryBase.classList.remove('has-item');
          this.summaryBase.innerHTML = `<p class="charm-builder__summary-empty">Pick a base to start.</p>`;
        }

        // Charms
        this.summaryCharms.innerHTML = '';
        this.selectedCharms.forEach((charm, index) => {
          const li = document.createElement('li');
          li.innerHTML = `
            <img src="${charm.image}" alt="" width="36" height="36">
            <span class="name">${charm.title}</span>
            <span class="price">${this.formatMoney(charm.price)}</span>
            <button type="button" class="remove" aria-label="Remove ${charm.title}" data-index="${index}">&times;</button>
          `;
          li.querySelector('.remove').addEventListener('click', () => {
            this.resetCardUI(charm.uid);
            this.selectedCharms.splice(index, 1);
            this.renderSummary();
          });
          this.summaryCharms.appendChild(li);
        });

        // Total
        const base = this.selectedBase ? this.selectedBase.price : 0;
        const charmsTotal = this.selectedCharms.reduce((sum, c) => sum + c.price, 0);
        this.summaryTotal.textContent = this.formatMoney(base + charmsTotal);

        // Button state
        this.addButton.disabled = !this.selectedBase;
      }

      formatMoney(cents) {
        return '$' + (cents / 100).toFixed(2);
      }

      bindAddToCart() {
        this.addButton.addEventListener('click', () => this.addBuildToCart());
      }

      addBuildToCart() {
        if (!this.selectedBase) return;

        this.addButton.setAttribute('aria-disabled', 'true');
        this.addButton.disabled = true;
        this.addButton.textContent = 'Adding…';
        this.setStatus('', '');

        const buildId = `build-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        const buildLabel = `Custom build — ${this.selectedBase.title}`;

        const items = [
          {
            id: this.selectedBase.id,
            quantity: 1,
            properties: {
              Piece: `Base — ${this.selectedBase.title}`,
              'Custom build': buildLabel,
              _build_id: buildId,
            },
          },
          ...this.selectedCharms.map((charm) => ({
            id: charm.id,
            quantity: 1,
            properties: {
              Piece: `Charm — ${charm.title}`,
              'Custom build': buildLabel,
              _build_id: buildId,
            },
          })),
        ];

        const body = { items };
        const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

        fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setStatus(response.description || response.message || 'Something went wrong. Please try again.', 'error');
              this.addButton.removeAttribute('aria-disabled');
              this.addButton.disabled = !this.selectedBase;
              this.addButton.textContent = 'Add build to cart';
              return;
            }

            this.setStatus('Added your build to cart! Refreshing…', 'success');
            this.resetCharms();
            setTimeout(() => {
              window.location.reload();
            }, 700);
          })
          .catch(() => {
            this.setStatus('Something went wrong. Please try again.', 'error');
            this.addButton.removeAttribute('aria-disabled');
            this.addButton.disabled = !this.selectedBase;
            this.addButton.textContent = 'Add build to cart';
          });
      }

      resetCharms() {
        this.charmCards.forEach((c) => this.resetCardUI(c));
        this.selectedCharms = [];
        this.renderSummary();
      }

      setStatus(message, type) {
        this.statusEl.textContent = message;
        this.statusEl.classList.remove('is-success', 'is-error');
        if (type) this.statusEl.classList.add(`is-${type}`);
      }
    }
  );
}
